import { useEffect, useMemo, useState } from 'react'
import { messageOf } from '../../lib/errors'
import type { OrgUnitOption, PersonDetail } from '../../lib/admin'
import { createOrgUnit, setEmployment, setWorkEmail } from '../../lib/employment'
import { PencilIcon } from '../icons'

/**
 * Who somebody is, where they sit, and how to reach them — the top block of the
 * admin panel, and the one an administrator opens the panel for (055).
 *
 * Was EmploymentEditor (049). Renamed and moved above Roles on David's call,
 * 11 Sep: role administration was taking most of the panel's height for the
 * thing that changes least, while the facts that change constantly sat under it.
 *
 * ── IT IS A REPLACE, AND THE FORM SAYS SO ───────────────────────────────────
 *
 * Every employment field posts on every save, because set_employment takes no
 * defaults: with them, clearing a job title and leaving it alone would be the
 * same request. That is why "Not set" is a real option on each select rather
 * than a separate button, and why Save is disabled until something has actually
 * changed — a form that always looks ready to save invites saving a record
 * somebody only opened to look at.
 *
 * ── THE EMAIL IS A SEPARATE WRITE, ON PURPOSE ───────────────────────────────
 *
 * It is one form and two RPCs, and folding the address into set_employment
 * would have been shorter and wrong twice over. First, set_employment is a
 * REPLACE: an admin fixing a job title would silently clear an address the
 * roster supplied. Second, they are different permissions on different tables —
 * and a contact detail is not an employment fact, it is how you reach the
 * person whether or not they have a record at all.
 *
 * So each half is sent only if that half changed. A pure-email edit never
 * touches employment, and a pure-placement edit never touches the address. They
 * are not atomic and cannot be, so when BOTH change and the second fails, the
 * error says which half landed rather than leaving the reader to guess.
 *
 * ── WORK EMAIL IS NOT A SIGN-IN ADDRESS ─────────────────────────────────────
 *
 * The single most likely misreading of this screen, so it is stated in the
 * field's own hint and again in the Sign-in section below. `identity` is the
 * boundary; this is a fact copied from BambooHR that authorises nothing. 880 of
 * the 931 active staff have one of these and six have an identity, so for
 * almost everybody this is the only address the hub knows — which is exactly
 * why it must not read as access.
 *
 * ── THE LEGAL ENTITY NARROWS THE BUSINESS UNIT, AND NOTHING ELSE ────────────
 *
 * 049's guard refuses a business unit that does not sit under the legal entity
 * beside it, so the form offers only what will be accepted, filtered on
 * `parentId` (049c) rather than a prefix match on the path. Changing the entity
 * clears the unit, visibly — leaving a stale one selected and letting the
 * server refuse the save would be technically safe and would read as a bug.
 *
 * The DEPARTMENT is not narrowed and is not cleared (049d). It is global.
 *
 * A person with an entity and no business unit is a normal state, not an
 * incomplete one.
 *
 * ── A RECORD BAMBOO OWNS IS ONE TO THINK TWICE ABOUT ────────────────────────
 *
 * `externallyManaged` is the `external_ref` row (rule 7, and 045's
 * app.is_externally_managed is the single test for it). It does not disable
 * anything: the sync is suspend-only by decision (ACC-18) and an administrator
 * correcting a wrong title today is doing the right thing. It warns, because the
 * next reconcile will put it back and finding that out afterwards is worse than
 * being told. Since 055 that applies to the address too.
 */

const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'leave', label: 'On leave' },
  { value: 'terminated', label: 'Left the company' },
]

type Form = {
  legalEntityId: string
  businessUnitId: string
  departmentId: string
  jobTitle: string
  workEmail: string
  managerPersonId: string
  employmentStatus: string
  startedOn: string
}

/** The employment half of the form — everything set_employment replaces. */
const EMPLOYMENT_KEYS: (keyof Form)[] = [
  'legalEntityId',
  'businessUnitId',
  'departmentId',
  'jobTitle',
  'managerPersonId',
  'employmentStatus',
  'startedOn',
]

/**
 * Which units of a type sit directly under a legal entity.
 *
 * Exported because it is the half of this component a test can reach — the
 * suite renders to a string with no DOM, so a select's option list is not
 * observable there and this would otherwise be covered by nothing. It is also
 * where the interesting mistake lives: filtering by `path.startsWith(name)`
 * reads correctly and quietly offers "ConeTec Chile Norte"'s business units
 * inside "ConeTec Chile". 049c added `parentId` so this can be an identity
 * test rather than a string one.
 */
export function unitsUnder(
  orgUnits: OrgUnitOption[],
  type: 'business_unit' | 'department',
  legalEntityId: string,
): OrgUnitOption[] {
  // 049d: a DEPARTMENT is global and belongs to no entity. The 925-person
  // roster settled it — fourteen SF Group functional families, one of them
  // ("Operation Direction & Management") appearing in 35 of 37 divisions. So
  // the entity narrows the business units and must not narrow the departments.
  if (type === 'department') return orgUnits.filter((o) => o.type === 'department')
  if (legalEntityId === '') return []
  return orgUnits.filter((o) => o.type === type && o.parentId === legalEntityId)
}

/**
 * The record as a form. Exported for the same reason — this is the prefill, and
 * a prefill that quietly drops a field is how an edit clears something nobody
 * touched.
 */
export function formOf(detail: PersonDetail): Form {
  const e = detail.employment
  return {
    legalEntityId: e?.legalEntityId ?? '',
    businessUnitId: e?.primaryOrgUnitId ?? '',
    departmentId: e?.departmentId ?? '',
    jobTitle: e?.jobTitle ?? '',
    // Lives on person_contact, not employment — so it prefills whether or not
    // there is an employment record at all.
    workEmail: detail.workEmail ?? '',
    managerPersonId: e?.managerPersonId ?? '',
    // A record that does not exist yet is being created as an active one. That
    // is the only status that makes sense for a person an administrator is
    // sitting in front of; 'terminated' is something you change a record TO.
    employmentStatus: e?.employmentStatus ?? 'active',
    startedOn: e?.startedOn ?? '',
  }
}

export function ProfileEditor({
  detail,
  orgUnits,
  colleagues,
  externallyManaged,
  canCreateOrgUnit = true,
  onSaved,
}: {
  detail: PersonDetail
  orgUnits: OrgUnitOption[]
  /** Everyone else in the tenant, for the reporting line. */
  colleagues: { personId: string; displayName: string }[]
  externallyManaged: boolean
  /**
   * admin.org. Adding a department creates an org unit, and the org tree is
   * the tenant administrator's (137): an HR administrator picks from the
   * departments that exist.
   */
  canCreateOrgUnit?: boolean
  onSaved: () => Promise<void> | void
}) {
  const [form, setForm] = useState<Form>(() => formOf(detail))
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [newDepartment, setNewDepartment] = useState('')

  // Re-seed whenever the panel switches person, or the record is re-read after
  // a save. Without this the form keeps the previous colleague's job title and
  // the next save writes it onto them.
  useEffect(() => {
    setForm(formOf(detail))
    setEditing(false)
    setError(null)
    setNewDepartment('')
  }, [detail])

  const entities = useMemo(
    () => orgUnits.filter((o) => o.type === 'legal_entity'),
    [orgUnits],
  )
  const units = useMemo(
    () => unitsUnder(orgUnits, 'business_unit', form.legalEntityId),
    [orgUnits, form.legalEntityId],
  )
  const departments = useMemo(
    () => unitsUnder(orgUnits, 'department', form.legalEntityId),
    [orgUnits, form.legalEntityId],
  )

  const original = formOf(detail)
  const employmentDirty = EMPLOYMENT_KEYS.some((k) => form[k] !== original[k])
  const emailDirty = form.workEmail.trim() !== original.workEmail
  const dirty = employmentDirty || emailDirty

  function set<K extends keyof Form>(key: K, value: string) {
    setForm((f) => {
      const next = { ...f, [key]: value }
      // Changing the entity invalidates the business unit under it. Clearing it
      // here rather than letting the server refuse the save is the difference
      // between a form that guides and one that scolds.
      //
      // The DEPARTMENT is deliberately left alone (049d): moving somebody from
      // Lankelma to ConeTec does not stop them being in Human Resources, and
      // clearing it would quietly delete a fact nobody asked to change.
      if (key === 'legalEntityId') next.businessUnitId = ''
      return next
    })
  }

  async function save() {
    setBusy(true)
    setError(null)
    // Two RPCs on two permissions, so they cannot be one transaction. Each half
    // is sent only if it changed, which means the partial-failure window exists
    // only when both did — and in that case the message says which one landed.
    let employmentSaved = false
    try {
      if (employmentDirty) {
        await setEmployment(detail.personId, {
          legalEntityId: form.legalEntityId || null,
          businessUnitId: form.businessUnitId || null,
          departmentId: form.departmentId || null,
          jobTitle: form.jobTitle,
          managerPersonId: form.managerPersonId || null,
          employmentStatus: form.employmentStatus,
          startedOn: form.startedOn || null,
        })
        employmentSaved = true
      }
      if (emailDirty) {
        await setWorkEmail(detail.personId, form.workEmail)
      }
      await onSaved()
      setEditing(false)
    } catch (e) {
      setError(
        employmentSaved && emailDirty
          ? `Where they sit was saved. The email was not: ${messageOf(e)}`
          : messageOf(e),
      )
    } finally {
      setBusy(false)
    }
  }

  async function addDepartment() {
    setBusy(true)
    setError(null)
    try {
      const id = await createOrgUnit('department', newDepartment, null)
      setNewDepartment('')
      // The new unit is not in `orgUnits` until the parent refetches, so select
      // it optimistically and let the refresh catch up. onSaved() reloads both.
      setForm((f) => ({ ...f, departmentId: id }))
      await onSaved()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  if (!editing) {
    const e = detail.employment
    return (
      <>
        <div className="panel__factshead">
          {!e && !detail.workEmail && (
            <p className="empty">
              Nothing on file. No business unit, so audience targeting and the
              org filters cannot find them; no address, so there is nothing to
              invite.
            </p>
          )}
          <button
            type="button"
            className="btn btn--quiet btn--sm panel__edit"
            onClick={() => setEditing(true)}
          >
            <PencilIcon size={15} />
            <span>{e || detail.workEmail ? 'Edit' : 'Add details'}</span>
          </button>
        </div>

        <dl className="panel__facts">
          <dt>Job title</dt>
          <dd>{e?.jobTitle ?? '—'}</dd>
          <dt>Legal entity</dt>
          <dd>{e?.legalEntity ?? '—'}</dd>
          <dt>Business unit</dt>
          <dd>{e?.primaryOrgUnit ?? '—'}</dd>
          <dt>Department</dt>
          <dd>{e?.department ?? '—'}</dd>
          <dt>Work email</dt>
          <dd>
            {detail.workEmail ? (
              <span className="mono">{detail.workEmail}</span>
            ) : (
              <span className="admin__muted">none on file</span>
            )}
          </dd>
          <dt>Manager</dt>
          <dd>{e?.manager ?? '—'}</dd>
          <dt>Status</dt>
          <dd>
            {e
              ? STATUSES.find((s) => s.value === e.employmentStatus)?.label ??
                e.employmentStatus
              : '—'}
          </dd>
          {e?.startedOn && (
            <>
              <dt>Started</dt>
              <dd>{new Date(e.startedOn).toLocaleDateString()}</dd>
            </>
          )}
        </dl>

        {error && (
          <p className="roles__error" role="alert">
            {error}
          </p>
        )}
      </>
    )
  }

  return (
    <>
      {externallyManaged && (
        <p className="panel__rolecaution">
          BambooHR owns this record. An edit here holds until the next
          reconcile, which will put the roster's own answer back — including the
          work email.
        </p>
      )}

      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      <div className="admin__fields">
        <label className="admin__field">
          <span className="admin__searchlabel">Job title</span>
          <input
            type="text"
            value={form.jobTitle}
            onChange={(ev) => set('jobTitle', ev.target.value)}
            placeholder="Senior Driller"
            autoComplete="off"
            disabled={busy}
          />
        </label>

        <label className="admin__field">
          <span className="admin__searchlabel">Legal entity</span>
          <select
            value={form.legalEntityId}
            onChange={(ev) => set('legalEntityId', ev.target.value)}
            disabled={busy}
          >
            <option value="">Not set</option>
            {entities.map((o) => (
              <option key={o.orgUnitId} value={o.orgUnitId}>
                {o.name}
              </option>
            ))}
          </select>
        </label>

        <label className="admin__field">
          <span className="admin__searchlabel">Business unit</span>
          <select
            value={form.businessUnitId}
            onChange={(ev) => set('businessUnitId', ev.target.value)}
            disabled={busy || form.legalEntityId === ''}
          >
            <option value="">
              {form.legalEntityId === '' ? 'Choose a legal entity first' : 'Not set'}
            </option>
            {units.map((o) => (
              <option key={o.orgUnitId} value={o.orgUnitId}>
                {o.name}
              </option>
            ))}
          </select>
        </label>

        <label className="admin__field">
          <span className="admin__searchlabel">Department</span>
          <select
            value={form.departmentId}
            onChange={(ev) => set('departmentId', ev.target.value)}
            disabled={busy}
          >
            <option value="">Not set</option>
            {departments.map((o) => (
              <option key={o.orgUnitId} value={o.orgUnitId}>
                {o.name}
              </option>
            ))}
          </select>
        </label>

        <label className="admin__field">
          <span className="admin__searchlabel">Work email</span>
          <input
            type="email"
            value={form.workEmail}
            onChange={(ev) => set('workEmail', ev.target.value)}
            placeholder="name@conetec.com"
            autoComplete="off"
            disabled={busy}
          />
          {/* The likeliest misreading on this screen, so it is said here and
              again beside Sign-in. */}
          <span className="admin__hint">
            How the company reaches them. It is not what they sign in with — that
            is an invitation, below.
          </span>
        </label>

        <label className="admin__field">
          <span className="admin__searchlabel">Manager</span>
          <select
            value={form.managerPersonId}
            onChange={(ev) => set('managerPersonId', ev.target.value)}
            disabled={busy}
          >
            <option value="">Not set</option>
            {colleagues
              .filter((c) => c.personId !== detail.personId)
              .map((c) => (
                <option key={c.personId} value={c.personId}>
                  {c.displayName}
                </option>
              ))}
          </select>
        </label>

        <label className="admin__field">
          <span className="admin__searchlabel">Status</span>
          <select
            value={form.employmentStatus}
            onChange={(ev) => set('employmentStatus', ev.target.value)}
            disabled={busy}
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>

        <label className="admin__field">
          <span className="admin__searchlabel">Started</span>
          <input
            type="date"
            value={form.startedOn}
            onChange={(ev) => set('startedOn', ev.target.value)}
            disabled={busy}
          />
        </label>
      </div>

      {departments.length === 0 && (
        <p className="panel__note">
          No departments exist yet. A department is Bamboo's own field and is
          global — the roster carries fourteen of them and they span every legal
          entity, so it is not a subdivision of one.
        </p>
      )}

      {canCreateOrgUnit && (
      <div className="admin__fields">
        <label className="admin__field">
          <span className="admin__searchlabel">Add a department</span>
          <input
            type="text"
            value={newDepartment}
            onChange={(ev) => setNewDepartment(ev.target.value)}
            placeholder="Field Operations"
            autoComplete="off"
            disabled={busy}
          />
        </label>
        <button
          type="button"
          className="btn btn--quiet btn--sm"
          disabled={busy || newDepartment.trim() === ''}
          onClick={addDepartment}
        >
          Add
        </button>
      </div>
      )}

      <p className="panel__note">
        Saving writes the whole employment record, so a field left empty is
        cleared. The business unit here is what audience targeting and the org
        filters read.
      </p>

      <div className="panel__confirmrow">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={busy || !dirty}
          onClick={save}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className="btn btn--quiet"
          disabled={busy}
          onClick={() => {
            setForm(formOf(detail))
            setEditing(false)
            setError(null)
          }}
        >
          Cancel
        </button>
      </div>
    </>
  )
}
